/**
 * Test type constants and type definitions
 *
 * Centralized location for test type values used across:
 * - Database schema
 * - Frontend components
 * - Backend API handlers
 * - Test harness
 * - Import scripts
 */

/**
 * Test configuration types:
 * - FUNCTION: Standard function-based test (call function, check return)
 * - CLASS: Class-based test (instantiate class, call methods in sequence)
 * - RAW: Raw test scripts written by admins (student code prepended, scripts run directly)
 */
export const TEST_TYPE = {
  FUNCTION: 'function',
  CLASS: 'class',
  RAW: 'raw',
} as const

/**
 * Type for test type values
 */
export type TestType = (typeof TEST_TYPE)[keyof typeof TEST_TYPE]

/**
 * Default test type when not specified
 */
export const DEFAULT_TEST_TYPE = TEST_TYPE.FUNCTION

/**
 * Type guard: Check if a value is a valid test type
 */
export function isValidTestType(value: unknown): value is TestType {
  return (
    value === TEST_TYPE.FUNCTION ||
    value === TEST_TYPE.CLASS ||
    value === TEST_TYPE.RAW
  )
}

/**
 * Type guard: Check if test type is class-based
 */
export function isClassTestType(testType: TestType | undefined): boolean {
  return testType === TEST_TYPE.CLASS
}

/**
 * Type guard: Check if test type is function-based
 */
export function isFunctionTestType(testType: TestType | undefined): boolean {
  return testType === TEST_TYPE.FUNCTION || testType === undefined
}

/**
 * Type guard: Check if test type is raw (admin-authored test scripts)
 */
export function isRawTestType(testType: TestType | undefined): boolean {
  return testType === TEST_TYPE.RAW
}
